import { fetchInterestRate } from '@hex/calculators-shared/api';
import { DevLanguageSwitcher, displayLanguageSwitcher, ResetSitecoreStyles } from '@hex/calculators-shared/components';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { BaseLayout, FormLayout, ResultLayout } from '@hex/calculators-shared/layout';
import { ReferenceInterestRateResponse } from '@hex/calculators-shared/types';
import { getPartnership } from '@hex/calculators-shared/utils';
import { trackCalculatorSubmit } from '@hex/shared/tracking';
import { defaultTheme, SapphireProvider } from '@sapphire-react/core';
import { useRef, useState } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { Form } from './components/Form';
import { Results } from './components/Results';
import { CostResponse, FormDataType } from './types';

export function App() {
  const [responseData, setResponseData] = useState<CostResponse | ReferenceInterestRateResponse | null>(null);
  const [formData, setFormData] = useState<FormDataType>();
  const [hasError, setHasError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { isExtraSmall, isSmall } = useSapphireMedia();

  const buttonRef = useRef<HTMLButtonElement>(null);

  async function onSubmit(data: FormDataType, isDirty: boolean, reset?: UseFormReset<FormDataType>) {
    if (!isDirty) {
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      const newResponseData = await fetchInterestRate({
        loanAmount: data.loanAmount,
        propertyValue: data.propertyValue,
        partnership: getPartnership(data.partnership),
      });

      setFormData(data);
      setResponseData(newResponseData);
      trackCalculatorSubmit(data.loanAmount, data.partnership);
      setIsLoading(false);

      reset?.(data);
    } catch (_e) {
      setIsLoading(false);
      setHasError(true);
    }
  }

  const handleFormChange = () => {
    setResponseData(null);
  };
  const hasSmallScreen = isExtraSmall || isSmall;

  return (
    <ResetSitecoreStyles>
      <span
        style={{ display: 'none' }}
        data-build-id={import.meta.env.NX_PUBLIC_BUILD_NUMBER}
        data-git-hash={import.meta.env.NX_PUBLIC_GIT_HASH}
      />
      <SapphireProvider theme={defaultTheme} locale={'sv-SE'} width={'100%'}>
        {displayLanguageSwitcher && <DevLanguageSwitcher languages={['se']} />}
        <BaseLayout>
          <FormLayout showRightBorderRadius={hasSmallScreen}>
            <Form
              onSubmit={onSubmit}
              buttonRef={buttonRef}
              responseData={responseData}
              onFormChange={handleFormChange}
              loading={isLoading}
            />
          </FormLayout>
          <ResultLayout
            showLeftBorderRadius={hasSmallScreen}
            isHidden={hasSmallScreen && !responseData && !isLoading && !hasError}
            focus={hasSmallScreen && !!responseData}
          >
            <Results responseData={responseData} hasError={hasError} isLoading={isLoading} formData={formData} />
          </ResultLayout>
        </BaseLayout>
      </SapphireProvider>
    </ResetSitecoreStyles>
  );
}
